import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Save, RefreshCw, Building2, DollarSign, Bell, User,
  Mail, Phone, Calendar, FileText, AlertCircle, CheckCircle
} from 'lucide-react';

const ResSettings = () => {
  const [settings, setSettings] = useState({
    // General Settings
    companyName: 'Real Estate Management',
    companyAddress: 'Riyadh, Saudi Arabia',
    companyPhone: '+966 11 123 4567',
    companyEmail: 'info@realestate.com',
    
    // Payment Settings
    defaultPaymentTerms: 'Monthly, due on 15th',
    lateFeePercentage: 2,
    lateFeeFixed: 500,
    gracePeriodDays: 5,
    autoGenerateInvoices: true,
    invoiceReminderDays: 7,
    
    // Contract Settings
    defaultContractDuration: '3 years',
    autoRenewalDefault: false,
    renewalNoticeDays: 90,
    securityDepositMonths: 3,
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    paymentReminders: true,
    contractExpiryAlerts: true,
    issueAlerts: true,
    
    // Property Settings
    defaultPropertyStatus: 'vacant',
    maintenanceAlertThreshold: 3,
    
    // Reporting Settings
    defaultReportFormat: 'PDF',
    autoGenerateReports: false,
    reportSchedule: 'monthly',
  });

  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'payment', label: 'Payment', icon: DollarSign },
    { id: 'contract', label: 'Contract', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'property', label: 'Property', icon: Building2 },
  ];

  const handleSave = () => {
    // Save settings logic
    console.log('Saving settings:', settings);
    window.alert('Settings saved successfully!');
  };

  const handleReset = () => {
    // Reset to defaults
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      // Reset logic
      window.alert('Settings reset to defaults');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Settings</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure real estate management preferences
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-amber-100 text-amber-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">General Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Company Name</label>
                    <input
                      type="text"
                      value={settings.companyName}
                      onChange={(e) => updateSetting('companyName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Company Address</label>
                    <input
                      type="text"
                      value={settings.companyAddress}
                      onChange={(e) => updateSetting('companyAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone</label>
                      <input
                        type="text"
                        value={settings.companyPhone}
                        onChange={(e) => updateSetting('companyPhone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Email</label>
                      <input
                        type="email"
                        value={settings.companyEmail}
                        onChange={(e) => updateSetting('companyEmail', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payment' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Payment Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Default Payment Terms</label>
                    <input
                      type="text"
                      value={settings.defaultPaymentTerms}
                      onChange={(e) => updateSetting('defaultPaymentTerms', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Late Fee Percentage (%)</label>
                      <input
                        type="number"
                        value={settings.lateFeePercentage}
                        onChange={(e) => updateSetting('lateFeePercentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Fixed Late Fee (SAR)</label>
                      <input
                        type="number"
                        value={settings.lateFeeFixed}
                        onChange={(e) => updateSetting('lateFeeFixed', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Grace Period (Days)</label>
                    <input
                      type="number"
                      value={settings.gracePeriodDays}
                      onChange={(e) => updateSetting('gracePeriodDays', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.autoGenerateInvoices}
                      onChange={(e) => updateSetting('autoGenerateInvoices', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Auto-generate invoices</label>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Invoice Reminder (Days Before Due)</label>
                    <input
                      type="number"
                      value={settings.invoiceReminderDays}
                      onChange={(e) => updateSetting('invoiceReminderDays', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contract' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Contract Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Default Contract Duration</label>
                    <input
                      type="text"
                      value={settings.defaultContractDuration}
                      onChange={(e) => updateSetting('defaultContractDuration', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.autoRenewalDefault}
                      onChange={(e) => updateSetting('autoRenewalDefault', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Default auto-renewal enabled</label>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Renewal Notice Period (Days)</label>
                    <input
                      type="number"
                      value={settings.renewalNoticeDays}
                      onChange={(e) => updateSetting('renewalNoticeDays', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Security Deposit (Months)</label>
                    <input
                      type="number"
                      value={settings.securityDepositMonths}
                      onChange={(e) => updateSetting('securityDepositMonths', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Notification Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                      <p className="text-xs text-gray-500">Receive notifications via email</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">SMS Notifications</label>
                      <p className="text-xs text-gray-500">Receive notifications via SMS</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.smsNotifications}
                      onChange={(e) => updateSetting('smsNotifications', e.target.checked)}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Payment Reminders</label>
                      <p className="text-xs text-gray-500">Send reminders for upcoming payments</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.paymentReminders}
                      onChange={(e) => updateSetting('paymentReminders', e.target.checked)}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Contract Expiry Alerts</label>
                      <p className="text-xs text-gray-500">Alert when contracts are expiring</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.contractExpiryAlerts}
                      onChange={(e) => updateSetting('contractExpiryAlerts', e.target.checked)}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Issue Alerts</label>
                      <p className="text-xs text-gray-500">Notify about new issues and maintenance requests</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.issueAlerts}
                      onChange={(e) => updateSetting('issueAlerts', e.target.checked)}
                      className="w-4 h-4"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'property' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Property Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Default Property Status</label>
                    <select
                      value={settings.defaultPropertyStatus}
                      onChange={(e) => updateSetting('defaultPropertyStatus', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="vacant">Vacant</option>
                      <option value="occupied">Occupied</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Maintenance Alert Threshold</label>
                    <input
                      type="number"
                      value={settings.maintenanceAlertThreshold}
                      onChange={(e) => updateSetting('maintenanceAlertThreshold', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Number of issues before alert"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResSettings;
